import { ref, computed, readonly, inject, type Ref } from 'vue'
import { type AxiosError } from 'axios'

import type { CanvasRetrieve, User } from '@openapi'
import {
  ComponentTypes,
  type BlockProps,
  type CanvasBuilderProps,
  type ComponentNode,
} from '@canvas-builder/types/canvas'
import useCanvas from '@canvas-builder/composables/useCanvas'
import { useAppsDb } from '@lib/composables/appsDb.use'
import { APPS_DB } from '@lib/constants/appsDb.const'

const activeBlocks = ref<Record<CanvasRetrieve['id'], CanvasRetrieve>>({})

export const cleanup = () => {
  activeBlocks.value = {}
}

export default function useCanvasBlocks() {
  const { componentNodesById, mode } = useCanvas()
  const { getEntry, upsertEntry } =
    mode.value !== 'sharing' ? useAppsDb() : { getEntry: undefined, upsertEntry: undefined }
  const blockContentById = computed(() => {
    const blocks: Record<CanvasRetrieve['id'], ComponentNode[]> = {}

    Object.values(componentNodesById.value).forEach((node) => {
      if (node.type === ComponentTypes.Block) {
        const n = node as ComponentNode<BlockProps>
        const blockId = n.data?.id
        if (!blockId) return
        blocks[blockId] = activeBlocks.value[blockId]?.content?.data
      }
    })

    return blocks
  })
  const fetchCanvas = inject<CanvasBuilderProps['fetchCanvas']>('fetchCanvas')
  const myUser = inject<Ref<User | null>>(
    'myUser',
    computed(() => null),
  )
  const fetchBlock = async (blockId: string) => {
    if (!blockId || !fetchCanvas) throw new Error('Block ID is required')

    if (activeBlocks.value[blockId]) {
      return activeBlocks.value[blockId]
    }

    const block = await fetchCanvas(blockId)
    activeBlocks.value[blockId] = block
    return block
  }

  async function getUserBlockOverrides(blockId: string) {
    try {
      if (!myUser.value) throw new Error('myUser could not be injected')
      if (!getEntry) throw new Error('getEntry could not be used')

      const entry = await getEntry({
        store_name: APPS_DB.STORES.BLOCK_OVERRIDES,
        user_id: myUser.value.id,
        entry_key: `${APPS_DB.ENTRIES.BLOCK_OVERRIDES}_${blockId.toLowerCase()}`,
      })

      return entry?.value ?? null
    } catch (error) {
      if ((error as AxiosError)?.status !== 404) console.error('Error loading block overrides:', error)
      return null
    }
  }

  async function saveUserBlockOverrides(blockId: string, overrides: Record<string, any>) {
    try {
      if (!myUser.value) throw new Error('myUser could not be injected')
      if (!blockId) throw new Error('Block ID is required')
      if (!upsertEntry) throw new Error('upsertEntry could not be used')

      await upsertEntry({
        store_name: APPS_DB.STORES.BLOCK_OVERRIDES,
        user_id: myUser.value.id,
        entry_key: `${APPS_DB.ENTRIES.BLOCK_OVERRIDES}_${blockId.toLowerCase()}`,
        data: overrides,
      })
    } catch (error) {
      console.error('Error saving block overrides:', error)
    }
  }

  return {
    activeBlocks: readonly(activeBlocks),
    blockContentById: readonly(blockContentById),
    fetchBlock,
    getUserBlockOverrides,
    saveUserBlockOverrides,
    cleanup,
  }
}
